/**
 * @module cercaliagl/service/GeometryEntity
 */
import Service from './Service';

/**
 * <b>Javascript ES5</b>
 * ```js
 * const geomEntService = new cercaliagl.service.GeometryEntity();
 * ```
 *
 * Geometry entity service can obtain geometry giving different geo entities. Actually available
 * entities are:
 * - Postal codes
 * - Municipalities
 * - Districts
 *
 * Example usage:
 *
 * ```js
 * const geomEntService = new cercaliagl.service.GeometryEntity();
 * geomEntService.getPostalCodeGeometry('ESP', '08001', (data) => {
 *   const feature = new cercaliagl.Feature({
 *     wkt: geomEntService.getWKT()
 *   });
 *   map.addFeature(feature);
 *   map.centerToFeatures([feature]);
 * });
 * ```
 *
 * @classdesc
 * @extends {Service}
 * @api
 */
class GeometryEntity extends Service {
  constructor() {
    super();
    /**
     * @type {import('../servers').GeometryEntityResult|null}
     */
    this.response_ = null;
  }

  /**
   * Get postal code geometry in WKT format.
   * @param {string} countryId Country Id.
   * @param {string} postalCode Postal code.
   * @param {Function} callbackFn Callback Function to parse data server response.
   * @api
   */
  getPostalCodeGeometry(countryId, postalCode, callbackFn) {
    const params = { cmd: 'geoment', countryId, postalCode };
    this.getRequest({
      data: params,
      callbackFn: this.handleGeometryEntityResponse_.bind(this, callbackFn),
    });
  }

  /**
   * Get postal code geometry in WKT format.
   * @param {string} countryId Country Id.
   * @param {string} postalCode Postal code.
   * @param {Function} callbackFn Callback Function to parse data server response.
   * @api
   */
  getPostalCodeShortGeometry(pc2, callbackFn) {
    const params = { cmd: 'geoment', pc2 };
    this.getRequest({
      data: params,
      callbackFn: this.handleGeometryEntityResponse_.bind(this, callbackFn),
    });
  }

  /**
   * Get municipality geometry in WKT format.
   * @param {string} municipalityId Municipality.
   * @param {Function} callbackFn Callback function to parse data server response.
   * @api
   */
  getMunicipalityGeometry(municipalityId, callbackFn) {
    const params = { cmd: 'geoment', municipalityId };
    this.getRequest({
      data: params,
      callbackFn: this.handleGeometryEntityResponse_.bind(this, callbackFn),
    });
  }

  /**
   * Get district geometry in WKT format.
   * @param {string} districtId District id.
   * @param {Function} callbackFn Callback function to parse data server response.
   * @api
   */
  getDistrictGeometry(districtId, callbackFn) {
    const params = { cmd: 'geoment', districtId };
    this.getRequest({
      data: params,
      callbackFn: this.handleGeometryEntityResponse_.bind(this, callbackFn),
    });
  }

  /**
   * @private
   * @param {Function} callbackFn Callback functoin.
   * @param {import('../servers').GeometryEntityResult} data Server response
   */
  handleGeometryEntityResponse_(callbackFn, data) {
    this.response_ = data;
    if (callbackFn) {
      callbackFn(data);
    }
  }

  /**
   * @api
   * @return {import('../servers').GeometryEntityResult|null} Response
   */
  getResponse() {
    return this.response_;
  }

  /**
   * @api
   * @return {string|null} WKT last geometry requested.
   */
  getWKT() {
    try {
      return this.response_.cercalia.ge.geometry.wkt.value;
    } catch (e) {
      return null;
    }
  }
}

export default GeometryEntity;
